import { useState, useRef, useEffect } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { eventPathContains } from '../../utils';
import { useT } from '../../i18n';
import styles from './PositionMenu.module.css';

export interface PositionMenuProps {
  onPositionLeftHalf: () => void;
  onPositionRightHalf: () => void;
  onCollapseToDefault: () => void;
  textColor?: string;
}

/** Grid icon with the left half highlighted. */
function LeftHalfIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="2" width="6" height="12" rx="1" fill="currentColor" />
      <rect x="9" y="2" width="6" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** Grid icon with the right half highlighted. */
function RightHalfIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="2" width="6" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="2" width="6" height="12" rx="1" fill="currentColor" />
    </svg>
  );
}

export function PositionMenu({
  onPositionLeftHalf,
  onPositionRightHalf,
  onCollapseToDefault,
  textColor = '#1e293b',
}: PositionMenuProps) {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!eventPathContains(event, menuRef.current) && !eventPathContains(event, buttonRef.current)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (handler: () => void) => {
    handler();
    setIsOpen(false);
  };

  return (
    <div className={styles.container}>
      <button
        ref={buttonRef}
        className={styles.triggerButton}
        onClick={handleToggle}
        aria-label={t('position.menu')}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        style={{ color: `${textColor}80` }}
        type="button"
      >
        <Maximize2 size={18} />
      </button>

      {isOpen && (
        <div ref={menuRef} className={styles.menu} role="menu">
          <button
            className={styles.menuItem}
            onClick={() => handleOptionClick(onPositionLeftHalf)}
            role="menuitem"
            type="button"
          >
            <LeftHalfIcon size={16} />
            <span>{t('position.leftHalf')}</span>
          </button>
          <button
            className={styles.menuItem}
            onClick={() => handleOptionClick(onPositionRightHalf)}
            role="menuitem"
            type="button"
          >
            <RightHalfIcon size={16} />
            <span>{t('position.rightHalf')}</span>
          </button>
          <div className={styles.divider} />
          <button
            className={styles.menuItem}
            onClick={() => handleOptionClick(onCollapseToDefault)}
            role="menuitem"
            type="button"
          >
            <Minimize2 size={16} />
            <span>{t('position.defaultSize')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
